import { motion } from "framer-motion";
import { Clock, Facebook, Instagram, Mail, MapPin, Phone } from "lucide-react";
import EkkoLogo from "@/components/EkkoLogo";
import { ekkoText } from "@/lib/ekkoDefaults";
import { safeUrl } from "@/lib/whatsapp";

const HEADING = "text-lg font-bold text-[#A6C48A] tracking-wider mb-6";
const SOCIAL = "w-10 h-10 rounded-full border border-[#2A2A2A] flex items-center justify-center text-[#C9C9C9] hover:border-[#A6C48A] hover:text-[#A6C48A] transition-colors";

// Rodapé com a disposição da Durius e os dados da EKKO (painel → Contacto e Textos)
export default function Footer({ content = {}, navItems = [] }) {
  const scrollTo = (href) => document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  const address = ekkoText(content, "contact_address");
  const phone = ekkoText(content, "contact_phone");
  const email = ekkoText(content, "contact_email");
  const hours = ekkoText(content, "opening_hours");
  const instagram = safeUrl(ekkoText(content, "instagram_url"));
  const facebook = safeUrl(content.facebook_url);

  return (
    <footer className="bg-[#010000] border-t border-[#2A2A2A] pt-20 pb-8" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} transition={{ duration: 1 }} viewport={{ once: true }} className="flex justify-center mb-12">
          <EkkoLogo content={content} className="w-full max-w-xs md:max-w-sm h-auto text-[#A6C48A] select-none" />
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
          <div>
            <h3 className={HEADING}>NAVEGAÇÃO</h3>
            <ul className="space-y-3">
              {navItems.map((link) => (
                <li key={link.href}>
                  <button onClick={() => scrollTo(link.href)} className="text-[#C9C9C9] hover:text-[#A6C48A] transition-colors text-sm">
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className={HEADING}>CONTACTOS</h3>
            <ul className="space-y-4 text-[#C9C9C9] text-sm">
              {address && <li className="flex items-start gap-3"><MapPin size={18} className="text-[#A6C48A] flex-shrink-0 mt-0.5" /><span>{address}</span></li>}
              {phone && (
                <li className="flex items-center gap-3">
                  <Phone size={18} className="text-[#A6C48A]" />
                  <a href={`tel:${phone.replace(/[^\d+]/g, "")}`} className="hover:text-[#A6C48A] transition-colors">{phone}</a>
                </li>
              )}
              {email && (
                <li className="flex items-center gap-3">
                  <Mail size={18} className="text-[#A6C48A]" />
                  <a href={`mailto:${email}`} className="hover:text-[#A6C48A] transition-colors">{email}</a>
                </li>
              )}
              {hours && <li className="flex items-start gap-3"><Clock size={18} className="text-[#A6C48A] flex-shrink-0 mt-0.5" /><span>{hours}</span></li>}
            </ul>
          </div>

          <div>
            <h3 className={HEADING}>SEGUE-NOS</h3>
            <div className="flex gap-4 mb-8">
              {instagram && (
                <a href={instagram} target="_blank" rel="noopener noreferrer" className={SOCIAL} aria-label="Instagram do EKKO" data-testid="footer-instagram">
                  <Instagram size={18} />
                </a>
              )}
              {facebook && (
                <a href={facebook} target="_blank" rel="noopener noreferrer" className={SOCIAL} aria-label="Facebook do EKKO" data-testid="footer-facebook">
                  <Facebook size={18} />
                </a>
              )}
            </div>
            <p className="text-xl text-[#C9C9C9] italic rich-text-content" dangerouslySetInnerHTML={{ __html: ekkoText(content, "footer_tagline") }} />
          </div>
        </div>

        <div className="border-t border-[#2A2A2A] pt-8 flex flex-col md:flex-row items-center justify-between gap-4 text-[#9A9A9A] text-sm">
          <p className="rich-text-content" dangerouslySetInnerHTML={{ __html: ekkoText(content, "footer_text") }} />
          <p>{ekkoText(content, "brand_tagline")} · Maia, Portugal</p>
        </div>
      </div>
    </footer>
  );
}
